import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { withErrorBoundary } from 'react-error-boundary';
import { observer, useLocalStore } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Button, Text, TextVariants } from '@patternfly/react-core';
import { Link } from 'react-mobx-router5';
import SVG from 'react-inlinesvg';
import BaseCard from 'components/Card';
import { getOfferingTitleByLanguage } from 'services/UtilityService';
import ExpertIcon from 'img/icons/expertextra_small.svg';
import LockIcon from 'img/icons/lock.svg';
import { handleError } from 'services/ErrorService';
import { storesContext } from 'stores';
import { useFeature } from '@ole-ui/ole-ui-components';
import UnlockIcon from 'img/icons/unlock-icon.svg';
import CatalogStore from 'stores/Catalog';
import { EnablementFeatureFlags } from 'config/featureFlags';
import SeminarCollateral from './SeminarCollateral';
import ExpertSeminarStore from './store';

const ErrorMessage = () => {
  const { t } = useTranslation();

  return (
    <Alert bsStyle="danger">
      {t("Oops! We're having trouble displaying this card.")}
    </Alert>
  );
};

const ExpertExtrasCard = ({
  offering,
  actionButtonsArray,
  disableActionButtons,
  allowOrderingOffering,
  onOfferingOrderChange,
}) => {
  const { code, version, translations } = offering;

  const { i18n, t } = useTranslation();

  const store = useLocalStore(() => new ExpertSeminarStore());

  const { userStore } = useContext(storesContext);

  const subscribeToUnlockUrl =
    userStore.enablementsConfigurations?.subscription_to_unlock_url;

  const showSubscriptionToUnlockEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.ShowSubscriptionToUnlockEnabled,
    defaultFlagValue: false,
  });

  const isLocked = CatalogStore.isLocked(offering?.visibility);

  const showSubscriptionToUnlockButton =
    isLocked && showSubscriptionToUnlockEnabled;

  const lockedTooltip = (
    <Tooltip id="locked-tooltip">
      {t('This content is not available as part of your subscription')}
    </Tooltip>
  );

  const getIcon = () => {
    return !isLocked || showSubscriptionToUnlockButton ? (
      <SVG src={ExpertIcon} title={t('Expert Extra')} width={40} height={40} />
    ) : (
      <OverlayTrigger
        placement="bottom"
        trigger={['hover']}
        overlay={lockedTooltip}
      >
        <div className="content-locked">
          <SVG
            src={LockIcon}
            title={t('Content is locked')}
            width={40}
            height={40}
            style={{ pointerEvents: 'auto' }}
          />
        </div>
      </OverlayTrigger>
    );
  };

  const getTranslation = () => {
    return typeof translations === 'object'
      ? Object.values(translations)
      : translations;
  };

  const getTitle = () => {
    const title = getOfferingTitleByLanguage(
      offering,
      getTranslation(),
      i18n.language,
    );

    return title;
  };

  const getColorCard = () => {
    return '#5073B0';
  };

  const actionsForExpertSeminar = () => {
    let actionsToReturn = [];
    if (!isLocked) {
      if (actionButtonsArray?.length > 0) {
        actionsToReturn = actionButtonsArray;
        return actionsToReturn;
      }

      actionsToReturn.push(
        <Link
          className={`course-view ${
            disableActionButtons && 'course-actions-disabled'
          }`}
          href={`/seminar/${code}-${version}`}
          {...(disableActionButtons && { disabled: true })}
          routeName="expertSeminars:view"
          routeParams={{
            slug: `${code}-${version}`,
          }}
          data-analytics-id="view-btn-expert-card-lp"
        >
          {t('View').toUpperCase()}
        </Link>,
      );
    } else if (showSubscriptionToUnlockButton) {
      actionsToReturn.push(
        userStore.isRedHatLead ? (
          <Button
            href={subscribeToUnlockUrl}
            target="_blank"
            variant="link"
            component="a"
            icon={<SVG src={UnlockIcon} width={20} height={20} />}
            type="button"
            className="subscribe-to-unlock-button"
            data-analytics-id="subscribe-to-unlock-button-lp"
          >
            <span className="subscribe-to-unlock-text">
              {t('subscribe to access').toUpperCase()}
            </span>
          </Button>
        ) : (
          <Text component={TextVariants.h4}>
            {t('Contact your training partner to access')}
          </Text>
        ),
      );
    }

    return actionsToReturn;
  };

  return (
    <BaseCard
      customClassName="expert-extras-card"
      title={`${getTitle()}`}
      {...(showSubscriptionToUnlockButton && { titleColor: '#646464' })}
      cardColor={getColorCard()}
      iconClassName="expert-extras"
      svgIconElement={getIcon()}
      actionButtonsArray={actionsForExpertSeminar()}
      actionToggle={() => store.getSeminarCollateral(code, version)}
      allowOrderingOffering={allowOrderingOffering}
      onOfferingOrderChange={onOfferingOrderChange}
    >
      <div className="seminar-collateral">
        <React.Fragment>
          <SeminarCollateral collateral={store.collateral} />
        </React.Fragment>
      </div>
    </BaseCard>
  );
};

ExpertExtrasCard.propTypes = {
  offering: PropTypes.object,
  actionButtonsArray: PropTypes.array,
  disableActionButtons: PropTypes.bool,
  allowOrderingOffering: PropTypes.bool,
  onOfferingOrderChange: PropTypes.func,
};

ExpertExtrasCard.defaultProps = {
  offering: {},
  actionButtonsArray: [],
  disableActionButtons: false,
  allowOrderingOffering: false,
  onOfferingOrderChange: () => {},
};

export default withErrorBoundary(
  observer(ExpertExtrasCard),
  ErrorMessage,
  handleError,
);
